import { LoanRole } from '@matech/thebigpos-sdk'
import { captureException } from '@sentry/react'
import { useMutation } from '@tanstack/react-query'

import { TheBigPOSApi } from '../../../../lib/TheBigPOSClient'
import type { UserRole } from '../../../../components/modals/ModalShareLoan'
import type { UserGroupFormData } from '../../../../components/modals/ModalUserGroupsForm'

type CreateUserGroupMutationParams = {
	name: string
	description: string
}

type UpdateUserGroupMutationParams = CreateUserGroupMutationParams & {
	groupId: string
}

type RemoveUserGroupMemberMutationParams = {
	groupId: string
	userId: string
}

type CreateUserGroupMemberMutationParams =
	RemoveUserGroupMemberMutationParams & {
		loanRole: LoanRole
	}

const useUserGroupMembers = (
	refetchUserGroups: () => void,
	handleCloseModal: (
		modalType: 'members' | 'access' | 'delete'
	) => void,
	groupMembers?: UserRole[]
) => {
	// Mutation to create group
	const {
		mutateAsync: createUserGroupMutateAsync,
		isPending: isPendingCreatingGroup,
	} = useMutation({
		mutationFn: async ({
			name,
			description,
		}: CreateUserGroupMutationParams) => {
			const response = await TheBigPOSApi.createUserGroup({
				name,
				description,
			})

			return response.data
		},
	})

	// Mutation to update group
	const {
		mutateAsync: updateUserGroupMutateAsync,
		isPending: isPendingUpdatingGroup,
	} = useMutation({
		mutationFn: async ({
			groupId,
			name,
			description,
		}: UpdateUserGroupMutationParams) => {
			const response = await TheBigPOSApi.updateUserGroup(groupId, {
				name,
				description,
			})

			return response.data
		},
	})

	// Mutation to create group members
	const {
		mutateAsync: createUserGroupMemberMutateAsync,
		isPending: isPendingCreatingGroupMember,
	} = useMutation({
		mutationFn: async ({
			groupId,
			userId,
			loanRole,
		}: CreateUserGroupMemberMutationParams) => {
			const response = await TheBigPOSApi.createUserGroupMember(
				groupId,
				{
					userId,
					loanRole,
				}
			)

			return response.data
		},
	})

	// Mutation to delete group members
	const {
		mutateAsync: removeUserGroupMemberMutateAsync,
		isPending: isPendingRemoveGroupMember,
	} = useMutation({
		mutationFn: async ({
			groupId,
			userId,
		}: RemoveUserGroupMemberMutationParams) => {
			await TheBigPOSApi.deleteUserGroupMember(groupId, userId)
		},
	})

	const isSubmittingUserGroupMembers =
		isPendingCreatingGroup ||
		isPendingCreatingGroupMember ||
		isPendingUpdatingGroup ||
		isPendingRemoveGroupMember

	const createGroupMembers = async (data: UserGroupFormData) => {
		const { groupName, description, selectedUsers } = data

		const group = await createUserGroupMutateAsync({
			name: groupName,
			description,
		})

		await Promise.all(
			selectedUsers.map((selectedUser) =>
				createUserGroupMemberMutateAsync({
					groupId: group.id,
					userId: selectedUser.user.id,
					loanRole: selectedUser.assignedRole as LoanRole,
				})
			)
		)

		return group
	}

	const removeUsersFromGroup = async (
		groupId: string,
		usersToRemove: UserRole[]
	): Promise<void> => {
		if (usersToRemove.length === 0) return

		await Promise.all(
			usersToRemove.map((userToRemove) =>
				removeUserGroupMemberMutateAsync({
					groupId,
					userId: userToRemove.user.id,
				})
			)
		)
	}

	const updateUsersInGroup = async (
		groupId: string,
		usersToUpdate: UserRole[],
		newUsers: UserRole[]
	): Promise<void> => {
		if (usersToUpdate.length === 0) return

		await Promise.all(
			usersToUpdate.map((userToUpdate) =>
				removeUserGroupMemberMutateAsync({
					groupId,
					userId: userToUpdate.user.id,
				})
			)
		)

		await Promise.all(
			usersToUpdate.map((userToUpdate) => {
				const newUserData = newUsers.find(
					(newUser) => newUser.user.id === userToUpdate.user.id
				)

				if (newUserData) {
					return createUserGroupMemberMutateAsync({
						groupId,
						userId: userToUpdate.user.id,
						loanRole: newUserData.assignedRole as LoanRole,
					})
				}

				return Promise.resolve()
			})
		)
	}

	const addUsersToGroup = async (
		groupId: string,
		usersToAdd: UserRole[]
	): Promise<void> => {
		if (usersToAdd.length === 0) return

		await Promise.all(
			usersToAdd.map((userToAdd) =>
				createUserGroupMemberMutateAsync({
					groupId,
					userId: userToAdd.user.id,
					loanRole: userToAdd.assignedRole as LoanRole,
				})
			)
		)
	}

	// Edit group with members
	const editGroupMembers = async (
		id: string,
		data: UserGroupFormData
	) => {
		try {
			const { groupName, description, selectedUsers } = data
			const currentUsers = groupMembers || []
			const newUsers = selectedUsers || []

			const usersToRemove = currentUsers.filter(
				(currentUser) =>
					!newUsers.some(
						(newUser) => newUser.user.id === currentUser.user.id
					)
			)

			const usersToUpdate = currentUsers.filter((currentUser) => {
				const matchingNewUser = newUsers.find(
					(newUser) => newUser.user.id === currentUser.user.id
				)
				return (
					matchingNewUser &&
					matchingNewUser.assignedRole !== currentUser.assignedRole
				)
			})

			const usersToAdd = newUsers.filter(
				(newUser) =>
					!currentUsers.some(
						(currentUser) => currentUser.user.id === newUser.user.id
					)
			)

			await updateUserGroupMutateAsync({
				groupId: id,
				name: groupName,
				description,
			})

			await Promise.all([
				removeUsersFromGroup(id, usersToRemove),
				addUsersToGroup(id, usersToAdd),
			])

			await updateUsersInGroup(id, usersToUpdate, newUsers)
		} catch (error) {
			captureException(error)
		}
	}

	return {
		createGroupMembers,
		editGroupMembers,
		isSubmittingUserGroupMembers,
	}
}

export default useUserGroupMembers
